import { createContext, useContext } from "react";
import { Sparkles } from "lucide-react";

/**
 * Feb-2026 (User-Wunsch) — KI/MA-Feld-Markierung.
 *
 * Kleiner, dezenter Marker direkt am Feld-Label:
 *   • Orange Pille mit Stern + „KI"  → Wert wurde durch die KI ergänzt
 *     (ZB1/COC-Scan, KBA-/Werks-Lookup, Sprach-zu-Inserat, KI-Beschreibung).
 *     Tooltip: „Ergänzt durch KI, kann fehlerhaft sein".
 *   • Graue Pille mit „MA"           → Wert wurde anschließend manuell überschrieben.
 *     Tooltip: „Manuell erfasste Daten".
 *
 * Felder, die nie von der KI berührt wurden, zeigen KEINEN Marker.
 *
 * Die Quelle pro Feld kommt aus `FieldSourcesContext` (gepflegt in
 * CreateListingPage): { [fieldKey]: "ai" | "manual" }.
 */
export const FieldSourcesContext = createContext({});

export default function KiBadge({ field }) {
    const sources = useContext(FieldSourcesContext);
    const src = sources?.[field];
    if (src !== "ai" && src !== "manual") return null;

    const isAi = src === "ai";
    return (
        <span
            className={`inline-flex items-center gap-0.5 align-middle rounded-full pl-0.5 pr-1 h-[14px] text-[8px] font-black uppercase leading-none tracking-tight cursor-default select-none ${
                isAi ? "bg-[#F97316] text-white/90" : "bg-gray-400 text-white/90"
            }`}
            title={isAi ? "Ergänzt durch KI, kann fehlerhaft sein" : "Manuell erfasste Daten"}
            data-testid={`field-source-${field}`}
            data-source={src}
        >
            {isAi ? <Sparkles className="w-2 h-2" strokeWidth={2.5} /> : null}
            {isAi ? "KI" : "MA"}
        </span>
    );
}
